// Shared display-layer fixes for kenya-restyle.md §7 ("Content bugs
// seen during the pass"). Both bugs originate in server-supplied label
// strings (DataFileImportDto's *SectionDto.completionEstimateInLabel
// and fileSizeLabel are plain strings from the API, not computed in
// this app) so there is no client-side source value to recompute from
// scratch — these functions normalize whatever label text the server
// sent into one consistent, sane presentation.

/**
 * "29773 days 19 hours" / "Infinite" for a job that hasn't actually
 * started yet (0% progress -> division by zero on the server's rate
 * calculation). Shown as "Not started" until progress is above 0, and
 * capped at "> 30 days" for any genuinely large-but-real estimate.
 */
export function formatEtaLabel(label: string | undefined | null, progressPercentage: number | undefined | null): string {
	if (!progressPercentage || progressPercentage <= 0) {
		return 'Not started';
	}
	if (!label) {
		return '—';
	}
	if (/infinite|nan/i.test(label)) {
		return '> 30 days';
	}
	const daysMatch = label.match(/(\d+)\s*day/i);
	if (daysMatch && Number(daysMatch[1]) > 30) {
		return '> 30 days';
	}
	return label;
}

/**
 * Normalizes mixed-locale number/size labels ("46,8M", "20,3 KB",
 * "1000001", "189 bytes") to one locale: space thousands separators,
 * dot decimals ("1 000 001", "20.3 KB", "46.8M", "189 B").
 */
export function normalizeNumberLabel(label: string | undefined | null): string {
	if (!label) {
		return '—';
	}
	const trimmed = label.trim();

	const bytesMatch = trimmed.match(/^(\d+)\s*bytes?$/i);
	if (bytesMatch) {
		return `${groupThousands(bytesMatch[1])} B`;
	}

	const sizeMatch = trimmed.match(/^(\d+)[.,](\d+)\s*([a-zA-Z]+)$/);
	if (sizeMatch) {
		return `${sizeMatch[1]}.${sizeMatch[2]} ${sizeMatch[3].toUpperCase()}`;
	}

	const plainDigits = trimmed.match(/^\d+$/);
	if (plainDigits) {
		return groupThousands(trimmed);
	}

	return trimmed;
}

function groupThousands(digits: string): string {
	return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
